"use client";

import { Icon } from "@ospweb/sdk-ui";
import React from "react";
import { cn } from "utils";

const iconButtonClass =
  "flex size-10 shrink-0 items-center justify-center rounded-full bg-background_solid_neutral_primary shadow-[0px_2px_20px_0px_rgba(0,0,0,0.08)]";

export interface MobileStackScreenLayoutProps {
  showBack?: boolean;
  onBack?: () => void;
  onClose?: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  contentClassName?: string;
}

/**
 * 栈导航页布局组件：包含
 * 头部区域（支持返回按钮、关闭按钮）；
 * 内容区域；
 * 底部固定区域（支持主按钮）。
 */
export const MobileStackScreenLayout: React.FC<
  MobileStackScreenLayoutProps
> = ({
  showBack = false,
  onBack,
  onClose,
  children,
  footer,
  className,
  contentClassName,
}) => {
  return (
    <div className={cn("flex h-full min-h-0 w-full flex-col", className)}>
      <div
        className={cn(
          "flex shrink-0 items-center px-4 pb-3 pt-2",
          showBack ? "justify-between" : "justify-start",
        )}
      >
        {showBack ? (
          <button
            type="button"
            onClick={onBack}
            className={iconButtonClass}
            aria-label="Back"
          >
            <Icon
              name="left-arrow-alt"
              size={20}
              className="text-content_primary"
            />
          </button>
        ) : onClose ? (
          <button
            type="button"
            onClick={onClose}
            className={iconButtonClass}
            aria-label="Close"
          >
            <Icon name="close" size={20} className="text-content_primary" />
          </button>
        ) : (
          <span className="size-10" />
        )}

        {showBack && onClose && (
          <button
            type="button"
            onClick={onClose}
            className={iconButtonClass}
            aria-label="Close"
          >
            <Icon name="close" size={20} className="text-content_primary" />
          </button>
        )}
      </div>

      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto no-scrollbar",
          contentClassName,
        )}
      >
        {children}
      </div>

      {footer ? (
        <div className="shrink-0 border-t border-transparent">{footer}</div>
      ) : null}
    </div>
  );
};

export interface MobileStackPrimaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  children: React.ReactNode;
}

/**
 * 栈导航页底部固定区域的全宽主按钮，支持 `loading` / `disabled`。
 */
export const MobileStackPrimaryButton: React.FC<
  MobileStackPrimaryButtonProps
> = ({ loading, children, className, disabled, ...props }) => {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      data-loading={loading}
      className={cn(
        "button flex h-11 w-full items-center justify-center gap-1 rounded-lg button_m group",
        "bg-black text-content_inverse_primary",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
};
